import React, { useId } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';

import { WarningIcon } from '~/assets/images';
import MultiSelect from '~/components/General/MultiSelectNew';

const AddressEntryPanel = ({
  address,
  addressContactsPanelMessage,
  addressEntrySource,
  addressInputLabel,
  addressInputPlaceholder,
  addressInputRef,
  addressUploadDropzone,
  addressUploadMessage,
  contactOptions,
  contactsLoading,
  contactSearchFocusRequest,
  downloadSampleAddressUploadCsv,
  formatNumber,
  handleAddContactAddresses,
  handleAddressKeyDown,
  handleAddressEntrySourceSelect,
  hasContactSourceSelection,
  hasReachedMaxAddressEntries,
  hasReachedMaxSelectedAddresses,
  isAddingContactAddresses,
  isAddressInputSpotlighted,
  isFindHomesAroundAddressesMode,
  isProcessingAddressUpload,
  maxSelectedItemsPlaceholder,
  selectedContactIds,
  selectedTagContactsCount,
  selectedTagIds,
  setAddress,
  setContactAddressMessage,
  setContactSearch,
  setSelectedContactIds,
  setSelectedTagIds,
  setTagSearch,
  tagOptions,
  tagsLoading,
  uploadedAddressCount,
  uploadedAddressFileName,
}) => {
  const addressInputId = useId();
  const addressLimitNotice = hasReachedMaxAddressEntries ? (
    <Box className="addressLimitNotice" role="alert">
      <Typography>{maxSelectedItemsPlaceholder}</Typography>
    </Box>
  ) : null;

  return (
    <>
    {isFindHomesAroundAddressesMode && (
      <>
        <Typography className="addressEntrySourceTitle">How do you want to add addresses?</Typography>
        <Box className="addressEntrySourceTabs" aria-label="Address entry source">
          <button
            className={addressEntrySource === 'enter' ? 'addressEntrySourceTab selected' : 'addressEntrySourceTab'}
            onClick={() => handleAddressEntrySourceSelect('enter')}
            type="button"
          >
            <LocationOnOutlinedIcon />
            <span className="entryTabCopy">
              <span>Enter Addresses</span>
            </span>
          </button>
          <button
            className={addressEntrySource === 'upload' ? 'addressEntrySourceTab selected' : 'addressEntrySourceTab'}
            onClick={() => handleAddressEntrySourceSelect('upload')}
            type="button"
          >
            <UploadOutlinedIcon />
            <span className="entryTabCopy">
              <span>Upload Addresses</span>
            </span>
          </button>
          <button
            className={addressEntrySource === 'contacts' ? 'addressEntrySourceTab selected' : 'addressEntrySourceTab'}
            onClick={() => handleAddressEntrySourceSelect('contacts')}
            type="button"
          >
            <GroupsOutlinedIcon />
            <span className="entryTabCopy">
              <span>Select From Contacts</span>
            </span>
          </button>
        </Box>
      </>
    )}

    {addressEntrySource === 'upload' && isFindHomesAroundAddressesMode ? (
      <Box className="addressUploadPanel">
        <Box
          {...addressUploadDropzone.getRootProps()}
          className={[
            'addressUploadDropzone',
            addressUploadDropzone.isDragAccept ? 'dragActive' : '',
            addressUploadDropzone.isDragReject || addressUploadMessage ? 'dragReject' : '',
            hasReachedMaxSelectedAddresses || isProcessingAddressUpload ? 'disabled' : '',
          ].filter(Boolean).join(' ')}
        >
          <input {...addressUploadDropzone.getInputProps()} />
          <Box className="addressUploadIcon">
            <UploadOutlinedIcon />
          </Box>
          <Box className="addressUploadCopy">
            <Typography className="addressUploadTitle">
              {isProcessingAddressUpload
                ? 'Processing addresses'
                : addressUploadDropzone.isDragAccept
                  ? 'Drop the file here'
                  : 'Upload address file'}
            </Typography>
            <Typography className="addressUploadHint">
              Upload a CSV file with addresses.
            </Typography>
            {uploadedAddressFileName && (
              <Typography className="addressUploadFileName">
                {uploadedAddressFileName} · {formatNumber(uploadedAddressCount)} address{uploadedAddressCount === 1 ? '' : 'es'} found
              </Typography>
            )}
          </Box>
          <Button
            className={isProcessingAddressUpload ? 'selectAddressFileButton uploading' : 'selectAddressFileButton'}
            disabled={hasReachedMaxSelectedAddresses || isProcessingAddressUpload}
            type="button"
          >
            {isProcessingAddressUpload ? 'Processing' : 'Select File'}
          </Button>
        </Box>
        {addressLimitNotice}
        <Box className="addressSampleDownload">
          <button onClick={downloadSampleAddressUploadCsv} type="button">
            Download
          </button>
          <span> our sample address CSV file</span>
        </Box>
        {addressUploadMessage && (
          <Box className="errorMessageContainer">
            <Typography className="errorAlert alertText">
              <img
                className="addressUploadWarningIcon"
                src={WarningIcon}
                alt="warning"
              />
              {addressUploadMessage}
            </Typography>
          </Box>
        )}
      </Box>
    ) : addressEntrySource === 'contacts' && isFindHomesAroundAddressesMode ? (
        <Box className="addressContactsPanel">
        <Box className="addressContactsSelectorGrid">
          <Box
            className={[
              'addressContactsSelector',
              selectedContactIds.length ? 'selected' : '',
              hasReachedMaxSelectedAddresses || isAddingContactAddresses || selectedTagIds.length > 0
                ? 'disabled'
                : '',
            ].filter(Boolean).join(' ')}
          >
            <Box className="addressContactsSelectorCopy">
              <Box className="addressContactsSelectorIcon">
                <GroupsOutlinedIcon />
              </Box>
              <Box>
                <Typography className="addressContactsSelectorTitle">Search by Name or Address</Typography>
                <Typography className="addressContactsSelectorHint">
                  Select contacts that already have mailing addresses.
                </Typography>
              </Box>
            </Box>
            <Box data-multiselect-type="contacts">
              <MultiSelect
                options={contactOptions}
                selectedValue={selectedContactIds}
                setSelectedValue={(nextValue) => {
                  const nextContactIds = Array.isArray(nextValue) ? nextValue : [];

                  setSelectedContactIds(nextContactIds);
                  setContactAddressMessage('');
                }}
                clearable={true}
                placeHolderText="Contacts"
                noOptionText="No contacts found"
                component="multipleIds"
                searchAutocomplete
                type="remove"
                searchFieldPlaceholderText="by name or address"
                disabled={hasReachedMaxSelectedAddresses || isAddingContactAddresses || selectedTagIds.length > 0}
                onSearchChange={setContactSearch}
                searchLoading={contactsLoading}
                searchFocusRequest={contactSearchFocusRequest}
                className={selectedContactIds.length > 0 ? 'activeState' : 'inactiveState'}
                activeState={true}
              />
            </Box>
          </Box>
          <Box
            className={[
              'addressContactsSelector',
              selectedTagIds.length ? 'selected' : '',
              hasReachedMaxSelectedAddresses || isAddingContactAddresses || selectedContactIds.length > 0
                ? 'disabled'
                : '',
            ].filter(Boolean).join(' ')}
          >
            <Box className="addressContactsSelectorCopy">
              <Box className="addressContactsSelectorIcon">
                <GroupsOutlinedIcon />
              </Box>
              <Box>
                <Typography className="addressContactsSelectorTitle">Search by Tag</Typography>
                <Typography className="addressContactsSelectorHint">
                  Pull contact addresses from a selected tag.
                </Typography>
              </Box>
            </Box>
            <Box data-multiselect-type="tags">
              <MultiSelect
                options={tagOptions}
                selectedValue={selectedTagIds}
                setSelectedValue={(nextValue) => {
                  const nextTagIds = Array.isArray(nextValue) ? nextValue : [];

                  setSelectedTagIds(nextTagIds);
                  setContactAddressMessage('');
                }}
                clearable={true}
                placeHolderText="Tags"
                noOptionText="No tags found"
                component="multipleIds"
                searchAutocomplete
                type="remove"
                searchFieldPlaceholderText="tag(s)"
                disabled={hasReachedMaxSelectedAddresses || isAddingContactAddresses || selectedContactIds.length > 0}
                onSearchChange={setTagSearch}
                searchLoading={tagsLoading}
                className={selectedTagIds.length > 0 ? 'activeState' : 'inactiveState'}
                activeState={true}
              />
              {selectedTagIds.length > 0 && (
                <Typography className="addressContactsCount">
                  {selectedTagContactsCount.isLoaded
                    ? `Addresses to add: ${formatNumber(selectedTagContactsCount.addressTotal)} (${formatNumber(selectedTagContactsCount.total)} ${selectedTagContactsCount.total === 1 ? 'contact' : 'contacts'})`
                    : 'Loading contact count...'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          className={[
            addressLimitNotice ? 'addressContactsLimitActionRow' : 'addressContactsActionRow',
            !addressContactsPanelMessage ? 'noContactsMessage' : '',
          ].filter(Boolean).join(' ')}
        >
          {addressLimitNotice}
          <Button
            className="addContactAddressesButton"
            disabled={!hasContactSourceSelection || hasReachedMaxSelectedAddresses || isAddingContactAddresses}
            onClick={() => handleAddContactAddresses()}
            type="button"
          >
            {isAddingContactAddresses ? 'Adding' : 'Add selected contacts'}
          </Button>
        </Box>
        {addressContactsPanelMessage && (
          <Typography className="addressContactsMessage">{addressContactsPanelMessage}</Typography>
        )}
      </Box>
    ) : (
      <>
        {!isFindHomesAroundAddressesMode && (
          <Typography className="addressInputLabel" component="label" htmlFor={addressInputId}>
            {addressInputLabel}
          </Typography>
        )}
        <Box
          className={[
            'addressInputRow',
            isAddressInputSpotlighted ? 'focusedAddressInputRow' : '',
            hasReachedMaxAddressEntries ? 'hasAddressLimitNotice' : '',
            address.trim() ? 'hasAddressValue' : '',
          ].filter(Boolean).join(' ')}
        >
          <TextField
            fullWidth
            id={addressInputId}
            inputRef={addressInputRef}
            inputProps={
              isFindHomesAroundAddressesMode ? { 'aria-label': addressInputLabel } : undefined
            }
            placeholder={addressInputPlaceholder}
            disabled={hasReachedMaxAddressEntries}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            onKeyDown={handleAddressKeyDown}
            InputProps={{
              startAdornment: <LocationOnOutlinedIcon className="addressSearchIcon" />,
            }}
          />
          {addressLimitNotice}
        </Box>
      </>
    )}
    </>
  );
};

export default AddressEntryPanel;
